import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { get } from 'lodash';
import { useDeepCompareEffect } from '@oscar/utils';
import { useFormContext } from '@oscar/form';
import { REQUEST_STATUS } from 'common/constants';
import { State, RequestType, Status } from 'features/insulation/typed';
import { useQuery } from 'hooks';
import { REQUEST_TYPE } from 'common/constants/insulation';

export type StatusInstance = {
  status?: Status;
  state?: State;
  type?: RequestType;
  id?: string;
};

const DEFAULT_STATUS = REQUEST_STATUS.DRAFT.VALUE;

export default function useStatus() {
  const [queries] = useQuery();
  const [status, setStatus] = useState<StatusInstance>();
  const [requestStatus, setRequestStatus] = useState<number>();
  const [formContext] = useFormContext();
  const formValues = formContext.selectors?.getInitalValue();
  const stringStatus = useMemo(() => {
    let result: Status | undefined;
    const { needToSubmittedTime, submittedTime } =
      formValues?.submitQAQCsTime || {};
    Object.keys(REQUEST_STATUS).forEach((key: string) => {
      const value: { VALUE: number; TEXT: string } = get(REQUEST_STATUS, key);
      if (value.VALUE === requestStatus) {
        if (
          needToSubmittedTime > 1 &&
          ['PENDING_PAINTING', 'PENDING_QAQC'].includes(key) &&
          needToSubmittedTime !== submittedTime
        ) {
          result = `${key}_${submittedTime + 1}` as Status;
        } else if (
          needToSubmittedTime > 1 &&
          ['PENDING_QAQC'].includes(key) &&
          needToSubmittedTime === submittedTime
        ) {
          result = `${key}_${needToSubmittedTime}` as Status;
        } else {
          result = key as Status;
        }
      }
    });
    return result;
  }, [requestStatus]);

  useEffect(() => {
    const newStatus = formValues?.status;
    if (newStatus) {
      setRequestStatus(newStatus);
    }
  }, [formValues]);

  const requestType = useMemo(() => {
    if (queries) {
      const link = queries?.query?.type;
      if (!link) {
        return undefined;
      }
      for (const key of Object.keys(REQUEST_TYPE)) {
        if (get(REQUEST_TYPE, key) === link) {
          return key as RequestType;
        }
      }
    }
    return undefined;
  }, [queries?.query]);

  const requestId = useMemo(() => {
    if (queries) {
      const id = get(queries?.params, 'id');
      if (!id) {
        return undefined;
      } else {
        return id;
      }
    }
    return undefined;
  }, [queries?.params]);

  const requestState = useMemo(() => {
    const { location } = queries;
    let state: State;

    if (
      location?.pathname &&
      (location.pathname.toLowerCase().includes('create') ||
        location.pathname.toLowerCase().includes('edit'))
    ) {
      if (location.pathname.toLowerCase().includes('create')) {
        setRequestStatus(DEFAULT_STATUS);
      }
      state = 'EDIT';
    } else {
      state = 'VIEW';
    }
    return state;
  }, [queries.location]);

  const changeStatus = useCallback((newStatus: number) => {
    setRequestStatus(newStatus);
  }, []);

  useDeepCompareEffect(() => {
    setStatus({
      status: stringStatus,
      state: requestState,
      type: requestType,
      id: requestId,
    });
  }, [stringStatus, requestType, requestState, requestId]);

  return [status, changeStatus] as [StatusInstance, typeof changeStatus];
}
